// =============================================================
// PERPETUUM — typed /api/v1 client (single source of truth = the OpenAPI
// contract in @perpetuum/server + the domain types here).
//
// UI-less + platform-less: built on the universal `fetch` (web + RN + Node),
// so both apps/web and apps/mobile consume ONE client. The shapes are the core
// domain types — no duplicated DTOs. Auth (Bearer JWT or X-API-Key) is injected
// via a token provider; the client never touches storage itself.
//
// The endpoints mirror api/openapi.ts:
//   - auth: login → mfa/verify → me / logout
//   - generic CRUD collections (devices, tasks, spares, …)
//   - domain-rule endpoints (spares book/receive/write-off/available,
//     tasks/:id/complete)
//   - sync upload + review-items
// =============================================================

import type {
  Device,
  Id,
  Lang,
  LocationNode,
  Spare,
  Stock,
  Task,
  User,
} from "../types/index.js";
import type { GroupPermissions } from "../rbac/index.js";

/** Universal fetch type (web/RN/Node ≥18 all expose a global `fetch`). */
type FetchLike = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
  },
) => Promise<{
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
  text: () => Promise<string>;
}>;

function defaultFetch(): FetchLike {
  const f = (globalThis as { fetch?: unknown }).fetch;
  if (typeof f !== "function") {
    throw new Error("ApiClient: no global fetch available; pass one in config.");
  }
  // Bind to globalThis: in the browser, `window.fetch` called with a `this`
  // other than `window` throws "Illegal invocation". Binding keeps it portable
  // across web/RN/Node (where fetch is context-free anyway).
  return (f as FetchLike).bind(globalThis as unknown as undefined) as FetchLike;
}

/** An error thrown for a non-2xx API response (carries the parsed body). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = "ApiError";
  }

  /** The server's machine-readable error code, when present. */
  get code(): string | undefined {
    const b = this.body as { error?: unknown } | null;
    return typeof b?.error === "string" ? b.error : undefined;
  }
}

export interface ApiClientConfig {
  /** Base URL of the API, e.g. "/api/v1" (same-origin) or "https://host/api/v1". */
  baseUrl?: string;
  /** Returns the current Bearer JWT (or null/undefined for unauthenticated). */
  getToken?: () => string | null | undefined;
  /** Or a scoped API key (X-API-Key) for machine clients. */
  apiKey?: string;
  /** Injected fetch (defaults to globalThis.fetch). */
  fetch?: FetchLike;
}

// --- Response shapes (mirror the server handlers) --------------------------

export interface LoginResponse {
  token: string;
  mfaRequired: boolean;
  user: { id: Id; name: string; companyId?: Id };
}
export interface MfaVerifyResponse {
  token: string;
  mfaRequired: boolean;
}
export interface MeResponse {
  kind: "user" | "api_key";
  id: Id;
  companyId?: Id;
  /** Human display name (users only; API keys may omit). */
  name?: string;
  /** Login username (users only). */
  username?: string;
  /** Contact email (users only; profile-drawer completeness). */
  email?: string;
  /** Owning company display name (resolved server-side). */
  companyName?: string;
  /** The user's primary RBAC group display name (groupIds[0]). */
  groupName?: string;
  permissions: GroupPermissions;
}
export interface ListResponse<T> {
  data: T[];
  /**
   * Total count of rows matching the query, IGNORING limit/offset — present on
   * the paged collections (tasks, generic CRUD list) so a client knows whether a
   * tail exists beyond the current window. Optional (older/derived endpoints omit
   * it); callers must never fabricate a total when it is absent.
   */
  total?: number;
  /** Echo of the applied page window (present alongside `total`). */
  limit?: number;
  offset?: number;
}
export interface SparesAvailableResponse {
  available: number;
}
/** Per-warehouse balance row for a spare (Available derived via core rule). */
export interface StockLevelRow {
  spareId: Id;
  stockId: Id;
  inStock: number;
  booked: number;
}
export interface SparesBookResponse {
  booked: number;
}
export interface SparesStockResponse {
  inStock: number;
  movementId: Id;
}
/** Result of a stock-to-stock transfer (both warehouses' new on-hand). */
export interface SparesTransferResponse {
  fromInStock: number;
  toInStock: number;
}

/** A spares Excel-import preflight report (UPDATE/CREATE/SECURITY-VIOLATION). */
export interface SparePreflightVerdict {
  entity: string;
  alldeviceId: string;
  tag: "CREATE" | "UPDATE" | "SECURITY-VIOLATION";
  existingId?: string;
  reason?: string;
}
export interface SparePreflightReport {
  verdicts: SparePreflightVerdict[];
  byEntity: Record<string, { total: number; create: number; update: number; securityViolation: number }>;
  summary: { total: number; create: number; update: number; securityViolation: number };
}

/** A work-order checklist item (technical step, LOTO-gated completion). */
export interface ChecklistItem {
  id: Id;
  taskId: Id;
  label: string;
  done: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * A logged work-time interval on a work order. A RUNNING interval has
 * `endedAt`/`durationMs` null; a closed one carries the server-computed
 * duration in milliseconds.
 */
export interface WorkTimeEntry {
  id: Id;
  taskId: Id;
  userId: Id | null;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
}

/** A file reference attached to a device (Device-Card Files tab). */
export interface DeviceFile {
  id: Id;
  deviceId: Id;
  url: string;
  filename: string;
  contentType: string | null;
  sizeBytes: number;
  createdAt?: string;
}

/** A measurement point on a device + its latest reading & tolerance band. */
export interface DeviceMeasurementPoint {
  id: Id;
  name: string;
  unit: string | null;
  ll: number;
  l: number;
  h: number;
  hh: number;
  templateId: Id;
  latestValue: number | null;
  latestRecordedAt: string | null;
  band: "ok" | "warn" | "alarm" | "none";
}

/** The stored reading + the band of its value (LL/L/H/HH). */
export interface MeasurementReadingResult {
  id: Id;
  value: number;
  band: "ok" | "warn" | "alarm" | "none";
  recordedAt: string;
}

/**
 * A measurement point as managed by the template schema-editor (Wave 7) — the
 * four threshold levels + identity. Distinct from {@link DeviceMeasurementPoint}
 * (which adds the device's latest reading & band); this is the editable record.
 */
export interface MeasurementPointRecord {
  id: Id;
  templateId: Id;
  name: string;
  unit: string | null;
  ll: number;
  l: number;
  h: number;
  hh: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * A persisted SELECT option on a custom-field def (Settings custom-field
 * editor), as returned over the wire — distinct from the domain `CustomFieldOption`
 * (which is the embedded shape on a CustomFieldDef). Mirrors MeasurementPointRecord.
 */
export interface CustomFieldOptionRecord {
  id: Id;
  fieldId: Id;
  value: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Result of converting a downtime (the fault register) into a work order.
 * The downtimes schema has NO taskId/convertedTaskId link column, so the server
 * returns the created task + the source downtime id (provenance) without a
 * persisted back-link.
 */
export interface DowntimeConvertResult {
  task: Task;
  downtimeId: Id;
}

/** A unified-timeline event on the Device-Card History tab. */
export interface DeviceHistoryEvent {
  kind: "task" | "downtime" | "movement" | "reading";
  at: string;
  title: string;
  detail?: string;
}

/** A sync mutation in the wire format the server's /sync/upload expects. */
export interface SyncMutationWire {
  opId: number;
  op: "PUT" | "PATCH" | "DELETE";
  table: string;
  id: string;
  data?: Record<string, unknown>;
  previousValues?: Record<string, unknown>;
  transactionId?: number;
}
export interface SyncUploadResult {
  opId: number;
  outcome: "applied" | "parked" | "skipped";
  reviewItemId?: Id;
  serverValues?: Record<string, unknown>;
  reason?: string;
}
export interface ReviewItem {
  id: Id;
  targetTable: string;
  targetId: string;
  op: "PUT" | "PATCH" | "DELETE";
  reason: "target_deleted" | "target_reassigned";
  status: "open" | "resolved";
  payload?: Record<string, unknown>;
  baseValues?: Record<string, unknown> | null;
  resolution?: "apply" | "discard" | null;
  createdAt: string;
}

/** One command-palette search hit (global chrome). */
export interface SearchHit {
  id: Id;
  kind: "device" | "task" | "spare" | "person";
  title: string;
  subtitle?: string;
  /** SPA navigation target. */
  path: string;
}

/** A group of search hits of one kind (devices / tasks / spares / people). */
export interface SearchGroup {
  kind: string;
  label: string;
  items: SearchHit[];
}

/** One derived notification (overdue WO / low stock / fault / parked sync). */
export interface AppNotification {
  /** Stable feed key (kind:entityId) — drives mark-read. */
  key: string;
  kind: "overdue_wo" | "low_stock" | "fault" | "sync_review";
  severity: "info" | "warning" | "critical";
  title: string;
  detail?: string;
  at: string;
  path?: string;
  read: boolean;
}

export interface NotificationsResponse {
  unread: number;
  data: AppNotification[];
}

/** The six report ids the /api/v1/reports/* endpoints expose. */
export type ReportId =
  | "mtbf"
  | "mttr"
  | "downtime"
  | "working-time"
  | "parts-spend"
  | "tasks-repairs";

/** Report filters mirrored from the server's ReportFilters. */
export interface ReportFilters {
  from?: string;
  to?: string;
  locationId?: Id;
  categoryId?: Id;
}

/** A computed report (mirrors the server's ReportResult). */
export interface ReportResult {
  id: ReportId;
  title: string;
  generatedAt: string;
  filters: ReportFilters;
  metrics: { label: string; value: number | null; unit?: string }[];
  columns: string[];
  rows: (string | number | null)[][];
}

/** A per-tenant feature-flag row (admin console). */
export interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

/** A CIDR allow/block rule (admin console). */
export interface IpRule {
  id: Id;
  kind: "allow" | "block";
  cidr: string;
  reason: string | null;
  active: boolean;
  createdAt?: string;
}

/** An audit-log entry (admin console security trail). */
export interface AuditEntry {
  id: Id;
  actorId: string | null;
  actorName: string | null;
  target: string | null;
  action: string;
  ip: string | null;
  fromMobile: boolean;
  createdAt: string;
}

/** Result of a mass-replace-performer call. */
export interface MassReplaceResult {
  affected: number;
  taskIds: Id[];
  dryRun: boolean;
}

/** Result of a mass-reschedule call (mirrors MassReplaceResult). */
export interface MassRescheduleResult {
  affected: number;
  taskIds: Id[];
  dryRun: boolean;
  /** Echo of the applied due date (ISO) so the caller can confirm/toast it. */
  dueDate: string;
}

/**
 * Filters for the audit-log query (admin console). All optional; `format=csv`
 * is handled by {@link ApiClient.auditLogExportPath} (an <a download>), not here.
 */
export interface AuditLogFilters {
  /** ISO date/time lower bound (inclusive). */
  from?: string;
  /** ISO date/time upper bound (inclusive). */
  to?: string;
  /** Filter by the acting principal's id. */
  actorId?: string;
  /** Case-insensitive substring match on the action text. */
  action?: string;
  limit?: number;
  offset?: number;
}

/** The verdict of testing an IP against the current rule set (admin IP screen). */
export interface IpTestResult {
  ip: string;
  allowed: boolean;
  reason: "allowlisted" | "blocked" | "default_allow";
  matchedAllow: string[];
  matchedBlock: string[];
}

/** A Dynamics 365 BC spare↔item mapping view (live state computed server-side). */
export interface BcMappingView {
  id: Id;
  connectionId: Id;
  spareId: Id;
  bcItemNo: string | null;
  state: "synced" | "out_of_sync" | "orphaned";
  cachedState: "synced" | "out_of_sync" | "orphaned";
  lastSyncedFingerprint: string | null;
  currentFingerprint: string;
  lastError: string | null;
}

/**
 * The typed Perpetuum API client. One instance per signed-in session (the web
 * app holds it in context; tests construct it against the in-process Hono app).
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetch: FetchLike;
  private readonly getToken?: () => string | null | undefined;
  private readonly apiKey?: string;

  constructor(config: ApiClientConfig = {}) {
    this.baseUrl = (config.baseUrl ?? "/api/v1").replace(/\/$/, "");
    this.fetch = config.fetch ?? defaultFetch();
    this.getToken = config.getToken;
    this.apiKey = config.apiKey;
  }

  /** Build auth + content headers for a request. */
  private headers(hasBody: boolean, tokenOverride?: string): Record<string, string> {
    const h: Record<string, string> = {};
    if (hasBody) h["content-type"] = "application/json";
    const token = tokenOverride ?? this.getToken?.();
    if (token) h["authorization"] = `Bearer ${token}`;
    else if (this.apiKey) h["x-api-key"] = this.apiKey;
    return h;
  }

  /** Core request helper — throws ApiError on non-2xx. */
  private async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; tokenOverride?: string } = {},
  ): Promise<T> {
    const hasBody = opts.body !== undefined;
    const res = await this.fetch(`${this.baseUrl}${path}`, {
      method,
      headers: this.headers(hasBody, opts.tokenOverride),
      ...(hasBody ? { body: JSON.stringify(opts.body) } : {}),
    });
    const parse = async (): Promise<unknown> => {
      const text = await res.text();
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    };
    const body = await parse();
    if (!res.ok) {
      throw new ApiError(res.status, body);
    }
    return body as T;
  }

  // ---- auth --------------------------------------------------------------

  login(username: string, password: string, companyId?: Id): Promise<LoginResponse> {
    return this.request<LoginResponse>("POST", "/auth/login", {
      body: { username, password, ...(companyId ? { companyId } : {}) },
    });
  }

  /** Verify the 6-digit MFA code. The pre-MFA token must be passed explicitly. */
  verifyMfa(code: string, preMfaToken: string): Promise<MfaVerifyResponse> {
    return this.request<MfaVerifyResponse>("POST", "/auth/mfa/verify", {
      body: { code },
      tokenOverride: preMfaToken,
    });
  }

  me(): Promise<MeResponse> {
    return this.request<MeResponse>("GET", "/auth/me");
  }

  logout(): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("POST", "/auth/logout", { body: {} });
  }

  // ---- generic CRUD ------------------------------------------------------

  list<T>(
    collection: string,
    params?: { limit?: number; offset?: number; status?: string; deviceId?: string },
  ): Promise<ListResponse<T>> {
    // Build the query string manually so core typechecks under a bare ES2022
    // lib (the mobile tsconfig has no DOM lib → no URLSearchParams global).
    const parts: string[] = [];
    if (params?.limit != null) parts.push(`limit=${encodeURIComponent(String(params.limit))}`);
    if (params?.offset != null) parts.push(`offset=${encodeURIComponent(String(params.offset))}`);
    // `status` is a single value OR a comma-separated list; the tasks collection
    // filters `status IN (...)`. Encode the whole value (commas survive
    // encodeURIComponent only as %2C — the server splits on the decoded comma).
    if (params?.status != null && params.status !== "")
      parts.push(`status=${encodeURIComponent(params.status)}`);
    // `deviceId` (tasks only) → server-side `device_id =` filter, so the asset
    // tree fetches one device's work orders instead of scanning the tenant.
    if (params?.deviceId != null && params.deviceId !== "")
      parts.push(`deviceId=${encodeURIComponent(params.deviceId)}`);
    const qs = parts.join("&");
    return this.request<ListResponse<T>>("GET", `/${collection}${qs ? `?${qs}` : ""}`);
  }

  get<T>(collection: string, id: Id): Promise<T> {
    return this.request<T>("GET", `/${collection}/${id}`);
  }

  create<T>(collection: string, body: Record<string, unknown>): Promise<T> {
    return this.request<T>("POST", `/${collection}`, { body });
  }

  update<T>(collection: string, id: Id, body: Record<string, unknown>): Promise<T> {
    return this.request<T>("PATCH", `/${collection}/${id}`, { body });
  }

  remove(collection: string, id: Id): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("DELETE", `/${collection}/${id}`);
  }

  // ---- typed convenience readers (used by the representative flows) -------

  locations(): Promise<ListResponse<LocationNode>> {
    return this.list<LocationNode>("locations", { limit: 500 });
  }
  devices(): Promise<ListResponse<Device>> {
    return this.list<Device>("devices", { limit: 500 });
  }
  tasks(params?: { limit?: number; offset?: number; status?: string; deviceId?: string }): Promise<ListResponse<Task>> {
    return this.list<Task>("tasks", { limit: 500, ...params });
  }
  spares(): Promise<ListResponse<Spare>> {
    return this.list<Spare>("spares", { limit: 500 });
  }
  stocks(): Promise<ListResponse<Stock>> {
    return this.list<Stock>("stocks", { limit: 100 });
  }
  users(): Promise<ListResponse<User>> {
    return this.list<User>("users", { limit: 200 });
  }

  // ---- spares domain-rule endpoints --------------------------------------

  spareAvailable(spareId: Id, stockId: Id): Promise<SparesAvailableResponse> {
    return this.request<SparesAvailableResponse>(
      "GET",
      `/spares/${spareId}/available?stockId=${encodeURIComponent(stockId)}`,
    );
  }
  /** Per-warehouse balances (in_stock + booked) for a spare. */
  spareLevels(spareId: Id): Promise<ListResponse<StockLevelRow>> {
    return this.request<ListResponse<StockLevelRow>>("GET", `/spares/${spareId}/levels`);
  }
  spareReceive(
    spareId: Id,
    body: { stockId: Id; quantity: number; supplier?: string; docNr?: string },
  ): Promise<SparesStockResponse> {
    return this.request<SparesStockResponse>("POST", `/spares/${spareId}/receive`, { body });
  }
  spareWriteOff(spareId: Id, body: { stockId: Id; quantity: number }): Promise<SparesStockResponse> {
    return this.request<SparesStockResponse>("POST", `/spares/${spareId}/write-off`, { body });
  }
  spareBook(spareId: Id, body: { stockId: Id; quantity: number }): Promise<SparesBookResponse> {
    return this.request<SparesBookResponse>("POST", `/spares/${spareId}/book`, { body });
  }
  /** Stock-to-stock transfer (booking-aware; server caps at Available). */
  spareTransfer(
    spareId: Id,
    body: { fromStockId: Id; toStockId: Id; quantity: number },
  ): Promise<SparesTransferResponse> {
    return this.request<SparesTransferResponse>("POST", `/spares/${spareId}/transfer`, { body });
  }
  /** Spares Excel-import preflight (UPDATE/CREATE/SECURITY-VIOLATION; no writes). */
  sparesImportPreflight(rows: { alldeviceId?: string; name?: string }[]): Promise<SparePreflightReport> {
    return this.request<SparePreflightReport>("POST", "/import/spares/preflight", { body: { rows } });
  }

  // ---- tasks domain-rule endpoints ---------------------------------------

  completeTask(taskId: Id): Promise<Task> {
    return this.request<Task>("POST", `/tasks/${taskId}/complete`, { body: {} });
  }
  /** Reschedule a task → writes tasks.due_date (optional weekend-avoidance). */
  rescheduleTask(taskId: Id, body: { dueDate: string; avoidWeekend?: boolean }): Promise<Task & { shifted: boolean }> {
    return this.request<Task & { shifted: boolean }>("POST", `/tasks/${taskId}/reschedule`, { body });
  }

  // ---- work-order execution surface (checklist + work timer) --------------

  /** A work order's checklist items, ordered by sortOrder. */
  taskChecklist(taskId: Id): Promise<ListResponse<ChecklistItem>> {
    return this.request<ListResponse<ChecklistItem>>("GET", `/tasks/${taskId}/checklist-items`);
  }
  /** Add a checklist item (sortOrder defaults to the end of the list). */
  addChecklistItem(taskId: Id, body: { label: string; sortOrder?: number }): Promise<ChecklistItem> {
    return this.request<ChecklistItem>("POST", `/tasks/${taskId}/checklist-items`, { body });
  }
  /** Update a checklist item (toggle done / rename / reorder). */
  updateChecklistItem(
    taskId: Id,
    itemId: Id,
    body: { done?: boolean; label?: string; sortOrder?: number },
  ): Promise<ChecklistItem> {
    return this.request<ChecklistItem>("PATCH", `/tasks/${taskId}/checklist-items/${itemId}`, { body });
  }
  /** Delete a checklist item. */
  deleteChecklistItem(taskId: Id, itemId: Id): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("DELETE", `/tasks/${taskId}/checklist-items/${itemId}`);
  }

  /** A work order's work-time intervals (newest first; running ⇒ endedAt null). */
  taskWorkTimes(taskId: Id): Promise<ListResponse<WorkTimeEntry>> {
    return this.request<ListResponse<WorkTimeEntry>>("GET", `/tasks/${taskId}/work-times`);
  }
  /** Start a work timer for the current user (422 already_running if one is open). */
  startWorkTime(taskId: Id): Promise<WorkTimeEntry> {
    return this.request<WorkTimeEntry>("POST", `/tasks/${taskId}/work-times/start`, { body: {} });
  }
  /** Stop the current user's running work timer (server endedAt + durationMs). */
  stopWorkTime(taskId: Id): Promise<WorkTimeEntry> {
    return this.request<WorkTimeEntry>("POST", `/tasks/${taskId}/work-times/stop`, { body: {} });
  }

  // ---- device-card writes (files, measurements, history, downtime) --------

  deviceFiles(deviceId: Id): Promise<ListResponse<DeviceFile>> {
    return this.request<ListResponse<DeviceFile>>("GET", `/devices/${deviceId}/files`);
  }
  addDeviceFile(
    deviceId: Id,
    body: { url: string; filename: string; contentType?: string; sizeBytes?: number },
  ): Promise<DeviceFile> {
    return this.request<DeviceFile>("POST", `/devices/${deviceId}/files`, { body });
  }
  deviceMeasurementPoints(deviceId: Id): Promise<ListResponse<DeviceMeasurementPoint>> {
    return this.request<ListResponse<DeviceMeasurementPoint>>("GET", `/devices/${deviceId}/measurement-points`);
  }
  addMeasurementReading(deviceId: Id, body: { pointId: Id; value: number }): Promise<MeasurementReadingResult> {
    return this.request<MeasurementReadingResult>("POST", `/devices/${deviceId}/measurement-readings`, { body });
  }

  // ---- measurement-template points (LL/L/H/HH schema editor, Wave 7) -------

  /** The points of a measurement template (ordered). Tenant-scoped via the template. */
  measurementPoints(templateId: Id): Promise<ListResponse<MeasurementPointRecord>> {
    return this.request<ListResponse<MeasurementPointRecord>>("GET", `/measurement-templates/${templateId}/points`);
  }
  /** Add a point to a template (thresholds must satisfy LL<=L<=H<=HH). */
  addMeasurementPoint(
    templateId: Id,
    body: { name: string; unit?: string | null; ll: number; l: number; h: number; hh: number },
  ): Promise<MeasurementPointRecord> {
    return this.request<MeasurementPointRecord>("POST", `/measurement-templates/${templateId}/points`, { body });
  }
  /** Update a point (rename / unit / thresholds). */
  updateMeasurementPoint(
    templateId: Id,
    pointId: Id,
    body: { name?: string; unit?: string | null; ll?: number; l?: number; h?: number; hh?: number },
  ): Promise<MeasurementPointRecord> {
    return this.request<MeasurementPointRecord>("PATCH", `/measurement-templates/${templateId}/points/${pointId}`, { body });
  }
  /** Delete a point. */
  deleteMeasurementPoint(templateId: Id, pointId: Id): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("DELETE", `/measurement-templates/${templateId}/points/${pointId}`);
  }
  deviceHistory(deviceId: Id): Promise<ListResponse<DeviceHistoryEvent>> {
    return this.request<ListResponse<DeviceHistoryEvent>>("GET", `/devices/${deviceId}/history`);
  }

  // ---- custom-field SELECT options (Settings custom-field editor, Wave 9) ---

  /** The persisted SELECT options of a custom-field def. Tenant-scoped via the def (dev:read). */
  customFieldOptions(fieldId: Id): Promise<ListResponse<CustomFieldOptionRecord>> {
    return this.request<ListResponse<CustomFieldOptionRecord>>("GET", `/custom-field-defs/${fieldId}/options`);
  }
  /** Add a SELECT option to a def (dev:write). Foreign fieldId 404s. */
  addCustomFieldOption(fieldId: Id, value: string): Promise<CustomFieldOptionRecord> {
    return this.request<CustomFieldOptionRecord>("POST", `/custom-field-defs/${fieldId}/options`, { body: { value } });
  }
  /** Remove a SELECT option (dev:write). Foreign fieldId/optionId 404s. */
  deleteCustomFieldOption(fieldId: Id, optionId: Id): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("DELETE", `/custom-field-defs/${fieldId}/options/${optionId}`);
  }

  /** Register (log) a downtime event (fires the downtime.opened webhook). */
  registerDowntime(body: {
    deviceId?: Id;
    categoryId?: Id;
    startedAt?: string;
    endedAt?: string;
    reason?: string;
  }): Promise<Record<string, unknown> & { id: Id }> {
    return this.request<Record<string, unknown> & { id: Id }>("POST", "/downtimes/register", { body });
  }

  /**
   * Convert a downtime (the fault register) into a work order (tasks:write).
   * The server seeds a task from the downtime (device + reason → title/desc,
   * status open). Tenant-scoped via the downtime row; a foreign downtimeId 404s.
   */
  convertDowntimeToTask(downtimeId: Id): Promise<DowntimeConvertResult> {
    return this.request<DowntimeConvertResult>("POST", `/downtimes/${downtimeId}/convert-to-task`, { body: {} });
  }

  // ---- sync (Review Center surface) --------------------------------------

  syncUpload(mutations: SyncMutationWire[]): Promise<{ results: SyncUploadResult[] }> {
    return this.request<{ results: SyncUploadResult[] }>("POST", "/sync/upload", {
      body: { mutations },
    });
  }
  reviewItems(status?: "open" | "resolved"): Promise<ListResponse<ReviewItem>> {
    const qs = status ? `?status=${status}` : "";
    return this.request<ListResponse<ReviewItem>>("GET", `/sync/review-items${qs}`);
  }
  resolveReviewItem(id: Id, resolution: "apply" | "discard"): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("POST", `/sync/review-items/${id}/resolve`, {
      body: { resolution },
    });
  }

  // ---- global chrome (Phase 9.3): command palette + notifications ----------

  /** Command-palette global search (assets / WOs / spares / people). */
  search(q: string, limit = 5): Promise<{ groups: SearchGroup[] }> {
    return this.request<{ groups: SearchGroup[] }>(
      "GET",
      `/search?q=${encodeURIComponent(q)}&limit=${encodeURIComponent(String(limit))}`,
    );
  }

  /** The derived notification feed + per-principal unread count. */
  notifications(limit = 50): Promise<NotificationsResponse> {
    return this.request<NotificationsResponse>("GET", `/notifications?limit=${encodeURIComponent(String(limit))}`);
  }

  /** Mark one notification (by key) — or all — read for the current principal. */
  markNotificationsRead(arg: { key: string } | { all: true }): Promise<{ ok: boolean; unread: number }> {
    return this.request<{ ok: boolean; unread: number }>("POST", "/notifications/read", { body: arg });
  }

  // ---- reports (real aggregation + export) -------------------------------

  /** List the available report ids. */
  reportIds(): Promise<ListResponse<ReportId>> {
    return this.request<ListResponse<ReportId>>("GET", "/reports");
  }

  /** Build the report query string from filters (+ optional format). */
  private reportQuery(filters?: ReportFilters, format?: "csv" | "pdf"): string {
    const parts: string[] = [];
    if (filters?.from) parts.push(`from=${encodeURIComponent(filters.from)}`);
    if (filters?.to) parts.push(`to=${encodeURIComponent(filters.to)}`);
    if (filters?.locationId) parts.push(`locationId=${encodeURIComponent(filters.locationId)}`);
    if (filters?.categoryId) parts.push(`categoryId=${encodeURIComponent(filters.categoryId)}`);
    if (format) parts.push(`format=${format}`);
    return parts.length ? `?${parts.join("&")}` : "";
  }

  /** Compute a report (JSON). */
  report(id: ReportId, filters?: ReportFilters): Promise<ReportResult> {
    return this.request<ReportResult>("GET", `/reports/${id}${this.reportQuery(filters)}`);
  }

  /** The download URL for a report export (CSV or PDF) — used by an <a download>. */
  reportExportPath(id: ReportId, format: "csv" | "pdf", filters?: ReportFilters): string {
    return `${this.baseUrl}/reports/${id}${this.reportQuery(filters, format)}`;
  }

  // ---- Dynamics 365 BC integration ---------------------------------------

  bcMappings(connectionId?: Id): Promise<ListResponse<BcMappingView>> {
    const qs = connectionId ? `?connectionId=${encodeURIComponent(connectionId)}` : "";
    return this.request<ListResponse<BcMappingView>>("GET", `/integrations/bc/mappings${qs}`);
  }
  bcLinkSpare(body: { connectionId: Id; spareId: Id; bcItemNo: string }): Promise<BcMappingView> {
    return this.request<BcMappingView>("POST", "/integrations/bc/mappings", { body });
  }
  bcSync(connectionId?: Id): Promise<{ results: unknown[] }> {
    return this.request<{ results: unknown[] }>("POST", "/integrations/bc/sync", {
      body: connectionId ? { connectionId } : {},
    });
  }

  // ---- admin console (feature flags, mass-replace, IP rules, audit) -------

  /** Read this tenant's per-module feature toggles. */
  featureFlags(): Promise<ListResponse<FeatureFlag>> {
    return this.request<ListResponse<FeatureFlag>>("GET", "/feature-flags");
  }
  /** Toggle a feature module on/off → the updated flag set. */
  setFeatureFlag(moduleId: string, enabled: boolean): Promise<ListResponse<FeatureFlag>> {
    return this.request<ListResponse<FeatureFlag>>("PUT", "/feature-flags", {
      body: { moduleId, enabled },
    });
  }

  /** Bulk-reassign upcoming tasks from one performer to another. */
  massReplacePerformer(body: {
    fromUserId: Id;
    toUserId: Id;
    locationId?: Id;
    categoryId?: Id;
    dryRun?: boolean;
  }): Promise<MassReplaceResult> {
    return this.request<MassReplaceResult>("POST", "/tasks/mass-replace-performer", { body });
  }

  /**
   * Bulk-reschedule upcoming tasks to a new due date. Mirrors
   * {@link massReplacePerformer}: the selector is EITHER `fromUserId` (every
   * non-completed task assigned to that performer, optionally scoped by
   * location/category — exactly like mass-replace) OR an explicit `taskIds`
   * list (the Work board's selected rows). `dryRun` previews the affected count
   * without writing. The server validates `dueDate` + requires tasks:write.
   */
  massReschedule(body: {
    dueDate: string;
    fromUserId?: Id;
    taskIds?: Id[];
    locationId?: Id;
    categoryId?: Id;
    avoidWeekend?: boolean;
    dryRun?: boolean;
  }): Promise<MassRescheduleResult> {
    return this.request<MassRescheduleResult>("POST", "/tasks/mass-reschedule", { body });
  }

  /** List CIDR allow ranges + the blocklist (optionally one kind). */
  ipRules(kind?: "allow" | "block"): Promise<ListResponse<IpRule>> {
    const qs = kind ? `?kind=${kind}` : "";
    return this.request<ListResponse<IpRule>>("GET", `/ip-rules${qs}`);
  }
  /** Add a CIDR allow range or a blocked address. */
  addIpRule(body: { kind: "allow" | "block"; cidr: string; reason?: string }): Promise<IpRule> {
    return this.request<IpRule>("POST", "/ip-rules", { body });
  }
  /** Remove an IP rule (unblock an address / drop an allow range). */
  removeIpRule(id: Id): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("DELETE", `/ip-rules/${id}`);
  }

  /**
   * Test an IP against the current rule set — would it be allowed or blocked?
   * Reuses the SAME core CIDR-match logic the rule-set evaluator uses (no
   * parallel implementation). admin:read.
   */
  testIp(ip: string): Promise<IpTestResult> {
    return this.request<IpTestResult>("POST", "/ip-rules/test", { body: { ip } });
  }

  /** Build the audit-log query string from filters (+ optional CSV format). */
  private auditQuery(filters?: AuditLogFilters, format?: "csv"): string {
    const parts: string[] = [];
    if (filters?.from) parts.push(`from=${encodeURIComponent(filters.from)}`);
    if (filters?.to) parts.push(`to=${encodeURIComponent(filters.to)}`);
    if (filters?.actorId) parts.push(`actorId=${encodeURIComponent(filters.actorId)}`);
    if (filters?.action) parts.push(`action=${encodeURIComponent(filters.action)}`);
    if (filters?.limit != null) parts.push(`limit=${encodeURIComponent(String(filters.limit))}`);
    if (filters?.offset != null) parts.push(`offset=${encodeURIComponent(String(filters.offset))}`);
    if (format) parts.push(`format=${format}`);
    return parts.length ? `?${parts.join("&")}` : "";
  }

  /**
   * Recent state-changing actions (security trail), newest first. Accepts the
   * same filters the server understands (date range, actor, action substring,
   * limit/offset). Passing a number keeps the old `auditLog(limit)` call-site
   * working.
   */
  auditLog(filters: AuditLogFilters | number = {}): Promise<ListResponse<AuditEntry>> {
    const f = typeof filters === "number" ? { limit: filters } : filters;
    return this.request<ListResponse<AuditEntry>>("GET", `/audit-log${this.auditQuery(f)}`);
  }

  /** The download URL for a CSV audit-log export (used by an <a download>). */
  auditLogExportPath(filters?: AuditLogFilters): string {
    return `${this.baseUrl}/audit-log${this.auditQuery(filters, "csv")}`;
  }

  /**
   * Fetch the audit-log CSV export as raw text (carrying the session auth
   * header — a bare <a href> can't). The caller turns it into a Blob download.
   * Throws ApiError on a non-2xx (e.g. 403 for a non-admin).
   */
  async auditLogCsv(filters?: AuditLogFilters): Promise<string> {
    const res = await this.fetch(`${this.baseUrl}/audit-log${this.auditQuery(filters, "csv")}`, {
      method: "GET",
      headers: this.headers(false),
    });
    const text = await res.text();
    if (!res.ok) {
      let body: unknown = text;
      try {
        body = JSON.parse(text);
      } catch {
        /* keep raw text */
      }
      throw new ApiError(res.status, body);
    }
    return text;
  }
}

/** Convenience factory. */
export function createApiClient(config: ApiClientConfig = {}): ApiClient {
  return new ApiClient(config);
}

/** Re-export Lang so apps can type the language switch without a deep import. */
export type { Lang };
